// 警報彈窗（LIVE-09；警報有〔已看到〕〔我來處理〕〔誤報〕，EVT-06）與感測器事件彈窗（plan/vms-web-v0.md DEV-12、§11.4 W08：門磁 / PIR 觸發 3 秒內彈窗）。
// 攝影機自己的事件：分級是警報（或使用者勾了偵測）→ 帶出來源攝影機。要彈哪些、彈到浮窗還是影像牆的指定格位看 lib/alarmPopup 的偏好。
// 即時串流收到感測器「觸發」（門開、有人）→ 有 popup 關聯的，帶出關聯攝影機，可一鍵跳到回放（事件前 5 秒）。沒有關聯的只出一則簡短提示。
//
// 浮動通知只有一套（VMS UX 評審第三輪 R3-P0-2、B3-02 / B3-03；SPEC UX-14 ⑥）：
//   - 右下角同時只顯示 1 張（最新的），其餘收成「還有 N 則警報 ▸」（到事件與警報 › 警報）；收件匣的浮動通知在浮窗展開時不跳
//   - **不自動播放即時影像**：先顯示事件那一刻的縮圖，按〔看即時〕才開串流（才算使用者觀看、才記稽核）
//   - 20 秒沒有操作、或換頁，就收成一條細條（〔展開〕〔✕〕）
//   - 標題 = 警報種類（事件顯示名）· 完整設備名；名稱在收到事件時就記下，切範圍也不掉；處理按鈕對範圍外的家戶照樣在，另給〔切到該範圍〕
import { useEffect, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { eventTsMs } from '@kinio/web-sdk';
import { useLiveEvents, type LiveEvent } from '../../lib/liveEvents';
import { useDevices } from '../../lib/useDevices';
import { fullName, sensorActive, sensorKind, type VmsDevice } from '../../lib/devices';
import { linkApi } from '../../lib/planeApi';
import { LiveTile } from '../live/LiveTile';
import { POPUP_COLLAPSE_MS, cameraPopupWanted, notePopped, popupToWall, poppedRecently, setPopupActive, subscribeInboxAlarms, usePopupPrefs, type InboxAlarm } from '../../lib/alarmPopup';
import { classify } from './EventsPage';
import { AlarmActions } from '../../components/AlarmActions';
import { EventThumb } from '../../components/EventThumb';
import { playbackHref } from '../../lib/links';
import { timeStamp } from '../../lib/timefmt';
import { eventName } from '../../lib/eventName';
import { useAlarmStates } from '../../lib/alarmStates';
import { savedToastLevel } from '../../lib/notifications';
import { useHomeLabel, useScope } from '../../lib/scope';
import { useEventWhat } from '../../lib/useEventWhat';

interface Popup {
  id: string;
  homeId: string;
  /** camera = 攝影機自己的事件（sensor 欄是來源攝影機） */
  kind?: 'camera';
  level?: string;
  sensor?: VmsDevice;
  sensorKey: string;
  /** 收到事件時記下的完整設備名（切範圍後設備清單裡找不到也照樣寫，B3-03） */
  name: string;
  /** 事件顯示名（「影像訊號中斷」；收件匣來的先放 notify 內文，讀到事件後換成事件的顯示名） */
  what: string;
  cameras: VmsDevice[];
  /** 關聯攝影機的完整名（同 name，收到時記下） */
  camNames: string[];
  at: number;
  receivedAt: number;
  /** 收件匣轉來的（notify；沒有 event_id 時不畫處理鈕） */
  fromInbox?: boolean;
}

/** 佇列最多幾則（其餘只在事件頁 / 收件匣）。 */
const QUEUE_MAX = 20;

export function SensorPopups() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const { subscribe } = useLiveEvents();
  const { tree, results } = useDevices();
  const { visible, homes, setScope } = useScope();
  const alarms = useAlarmStates();
  const [pops, setPops] = useState<Popup[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  /** 影像牆頁上新警報只讓細條閃一下（不整張展開蓋住格子，第四輪 R4-P0-1 ④）；數字變了就重播動畫 */
  const [flash, setFlash] = useState(0);
  const onWall = useRef(false);
  onWall.current = loc.pathname === '/live';
  const popsRef = useRef<Popup[]>([]);
  const [watching, setWatching] = useState<string | null>(null);
  const lastActive = useRef(Date.now());
  const [prefs] = usePopupPrefs();
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const lastCam = useRef(new Map<string, number>());
  const links = useQueries({
    queries: results.map((r) => ({
      queryKey: ['device-links', r.home.home_id],
      queryFn: () => linkApi.list(r.home.endpoints.api, r.home.home_id).catch(() => ({ data: [], can_write: false })),
      staleTime: 60_000,
    })),
  });

  const push = (p: Popup) => {
    if (savedToastLevel() === 'none') return; // 「都不跳」同時關掉警報卡（SPEC NOTIF-02 ①）；影像牆指定格位照常
    if (popsRef.current.some((x) => x.id && x.id === p.id)) return; // 同一則（串流與收件匣各來一次）
    notePopped(p.sensorKey, p.receivedAt);
    lastActive.current = Date.now();
    if (onWall.current) {
      // 影像牆：沒有卡片時從細條開始；已經展開的照舊，收著的不打開——細條閃一下
      if (!popsRef.current.length) setCollapsed(true);
      setFlash((n) => n + 1);
    } else {
      setCollapsed(false);
      setWatching(null);
    }
    setPops((l) => [p, ...l.filter((x) => x.sensorKey !== p.sensorKey)].slice(0, QUEUE_MAX));
  };
  const pushRef = useRef(push);
  pushRef.current = push;

  useEffect(() => {
    const byKey = new Map(tree.flatMap((d) => [d, ...d.children]).map((d) => [d.key, d]));
    return subscribe((e: LiveEvent) => {
      const key = `${e.homeId}/${e.deviceRef}`;
      const what = eventName(t, e.event.type, e.event.event_class, (e.event as { fields?: Record<string, unknown> }).fields ?? (e.event.payload as Record<string, unknown> | undefined), e.event.device_ref);
      // 攝影機自己的事件（LIVE-09）：警報 / 偵測照偏好帶出來源攝影機；同一台 30 秒內不重複彈
      const src = byKey.get(key);
      if (src?.isCamera) {
        const level = (e.event as { level?: string }).level ?? classify(e.event.type, e.event.event_class);
        if (!cameraPopupWanted(level, prefsRef.current) || e.event.phase === 'end') return;
        const now = Date.now();
        if (now - (lastCam.current.get(key) ?? 0) < 30_000) return;
        lastCam.current.set(key, now);
        if (prefsRef.current.target === 'cell' && popupToWall(key, prefsRef.current.cell)) return;
        const name = fullName(src, tree);
        pushRef.current({ id: e.event.id, homeId: e.homeId, kind: 'camera', level, sensor: src, sensorKey: key, name, what, cameras: [src], camNames: [name], at: eventTsMs(e.event), receivedAt: e.receivedAt });
        return;
      }
      if (!prefsRef.current.sensor) return;
      if (sensorActive(e.event.type, e.event.payload) !== true) return;
      const sensor = byKey.get(key);
      if (sensor && !sensorKind(sensor)) return;
      const i = results.findIndex((r) => r.home.home_id === e.homeId);
      const cams = (links[i]?.data?.data ?? [])
        .filter((l) => l.sensor_ref === e.deviceRef && l.purposes.includes('popup'))
        .map((l) => byKey.get(`${e.homeId}/${l.device_ref}`))
        .filter((d): d is VmsDevice => !!d)
        .sort((a, b) => (a.presence === 'online' ? 0 : 1) - (b.presence === 'online' ? 0 : 1)); // 在線的先（〔看即時〕開第一台）
      if (cams[0] && prefsRef.current.target === 'cell' && popupToWall(cams[0].key, prefsRef.current.cell)) return;
      pushRef.current({
        id: e.event.id,
        homeId: e.homeId,
        level: (e.event as { level?: string }).level,
        sensor,
        sensorKey: key,
        name: sensor ? fullName(sensor, tree) : key,
        what,
        cameras: cams,
        camNames: cams.map((c) => fullName(c, tree)),
        at: eventTsMs(e.event),
        receivedAt: e.receivedAt,
      });
    });
  }, [subscribe, tree, results, links, t]);

  // 收件匣（notify）來的警報類：同一套卡片（第四輪 R4-P0-1、B4-01 / B4-02）。串流剛帶出過同一台就不重複。
  // 只訂閱一次（資料從 ref 讀）：每次重畫都重訂的話，收件匣的 effect 可能剛好落在退訂與重訂之間，警報就掉回舊樣式
  const dataRef = useRef({ tree, results, links });
  dataRef.current = { tree, results, links };
  useEffect(() => {
    return subscribeInboxAlarms((a: InboxAlarm) => {
      const { tree, results, links } = dataRef.current;
      const byKey = new Map(tree.flatMap((d) => [d, ...d.children]).map((d) => [d.key, d]));
      const key = `${a.homeId}/${a.deviceRef}`;
      if (poppedRecently(key)) return;
      const src = byKey.get(key);
      const i = results.findIndex((r) => r.home.home_id === a.homeId);
      const linked = src?.isCamera
        ? [src]
        : (links[i]?.data?.data ?? [])
            .filter((l) => l.sensor_ref === a.deviceRef && l.purposes.includes('popup'))
            .map((l) => byKey.get(`${a.homeId}/${l.device_ref}`))
            .filter((d): d is VmsDevice => !!d);
      const name = src ? fullName(src, tree) : a.name || '…' + a.deviceRef.slice(-6);
      pushRef.current({
        id: a.eventId ?? 'inbox:' + a.nid,
        homeId: a.homeId,
        kind: src?.isCamera ? 'camera' : undefined,
        level: 'alarm',
        sensor: src,
        sensorKey: key,
        name,
        what: a.what,
        cameras: linked,
        camNames: linked.map((c) => fullName(c, tree)),
        at: a.at,
        receivedAt: Date.now(),
        fromInbox: true,
      });
    });
  }, []);

  // 沒有關聯畫面的感測器提示 15 秒自己消失；展開的卡片 20 秒沒操作就收成細條（滑鼠在上面、正在看即時不收）
  const hover = useRef(false);
  useEffect(() => {
    const iv = setInterval(() => {
      setPops((l) => {
        const n = l.filter((p) => p.cameras.length > 0 || p.level === 'alarm' || Date.now() - p.receivedAt < 15_000);
        return n.length === l.length ? l : n;
      });
      if (!hover.current && !watching && Date.now() - lastActive.current > POPUP_COLLAPSE_MS) setCollapsed(true);
    }, 1000);
    return () => clearInterval(iv);
  }, [watching]);
  // 換頁就收成細條（不蓋新頁面的主要動作）
  const firstPath = useRef(loc.pathname);
  useEffect(() => {
    if (loc.pathname === firstPath.current) return;
    firstPath.current = loc.pathname;
    setCollapsed(true);
    setWatching(null);
  }, [loc.pathname]);

  popsRef.current = pops;
  const head = pops[0];
  // 收件匣來的警報：種類名用事件本身的顯示名（跟清單、詳細、搜尋同一個；第七輪 R7-P0-1、B7-01）
  const headWhat = useEventWhat(head?.fromInbox ? head.homeId : undefined, head?.fromInbox ? head.id : undefined, head?.what ?? '');
  const homeLabel = useHomeLabel();
  // 卡片展開時頁面底部留卡片的高度（不蓋住最後幾列的〔回放〕；第七輪 B7-01）
  const cardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = cardRef.current;
    const root = document.documentElement;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => root.style.setProperty('--popup-h', `${Math.ceil(el.getBoundingClientRect().height) + 32}px`));
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--popup-h');
    };
  });
  // 範圍外的家戶也要能處理（AlarmActions 讀同一份狀態）
  const headHome = head?.homeId;
  useEffect(() => (headHome ? alarms.watch(headHome) : undefined), [headHome, alarms.watch]); // eslint-disable-line react-hooks/exhaustive-deps
  // 收件匣：浮窗展開時不跳浮動通知（同一個位置只有一張）
  const expandedCount = head && !collapsed ? 1 : 0;
  useEffect(() => setPopupActive(expandedCount), [expandedCount]);
  useEffect(() => () => setPopupActive(0), []);

  if (!head) return null;
  const p = { ...head, what: headWhat || head.what };
  const kind = p.sensor ? sensorKind(p.sensor) : null;
  const inScope = visible.some((h) => h.home_id === p.homeId);
  // 範圍外：卡上寫完整路徑（「LivingStyle › lstw › rd」），〔切到 …〕寫切到哪
  const homePath = homeLabel({ home_id: p.homeId, display_name: homes.find((h) => h.home_id === p.homeId)?.display_name });
  const close = () => {
    setWatching(null);
    setPops((l) => l.slice(1));
    lastActive.current = Date.now();
  };
  const touch = () => (lastActive.current = Date.now());
  const title =
    p.kind === 'camera' || p.fromInbox
      ? t(p.level === 'alarm' ? 'sensor.alarm_title2' : 'sensor.detection_title2', { what: p.what, name: p.name })
      : t(kind === 'motion' ? 'sensor.motion_title' : 'sensor.contact_title', { name: p.name });
  const icon = p.kind === 'camera' || p.fromInbox ? '⚠' : kind === 'motion' ? '👁' : '🚪';
  const more = pops.length - 1;
  const moreBtn = (short: boolean) =>
    more > 0 && (
      <button
        className="link-btn small"
        onClick={() => nav(p.level === 'alarm' ? '/events?tab=alarms' : '/events')}
        title={t('sensor.more_n', { count: more })}
        aria-label={t('sensor.more_n', { count: more })}
        data-testid="popup-more"
      >
        {short ? `+${more}` : t('sensor.more_n', { count: more })} ▸
      </button>
    );
  // 攝影機 / 收件匣的警報：標題分兩行「⚠ 警報 · <種類>」「<完整設備名>」；細條先寫設備名、種類放後面（第五輪 B5-09：320 px 的細條不再把名字截掉）
  const twoLine = p.kind === 'camera' || p.fromInbox;
  const kindLine = t(p.level === 'alarm' ? 'sensor.alarm_kind' : 'sensor.detection_kind', { what: p.what });

  if (collapsed) {
    return (
      <div className="sensor-popups" role="region" aria-label={t('sensor.popups')}>
        <div
          key={flash}
          className={'sensor-popup sensor-popup-bar card' + (flash ? ' flash' : '')}
          data-sensor={p.sensorKey}
          data-popup-kind={p.kind ?? (p.fromInbox ? 'inbox' : 'sensor')}
          data-collapsed="true"
        >
          {twoLine ? (
            <strong className="sensor-popup-title sp-bar-title" title={title}>
              <span className="sp-bar-name">
                {icon} {p.name}
              </span>
              <span className="sp-bar-what muted">{p.what}</span>
            </strong>
          ) : (
            <strong className="sensor-popup-title" title={title}>
              {icon} {title}
            </strong>
          )}
          {moreBtn(true)}
          <button className="icon-btn" onClick={() => (setCollapsed(false), touch())} aria-label={t('sensor.expand')} title={t('sensor.expand')} data-testid="popup-expand">
            ▴
          </button>
          <button className="icon-btn" aria-label={t('common.close')} onClick={close}>
            ✕
          </button>
        </div>
      </div>
    );
  }
  const cam = p.cameras[0];
  return (
    <div className="sensor-popups" role="region" aria-label={t('sensor.popups')}>
      <div
        ref={cardRef}
        className={`sensor-popup card ${p.cameras.length ? 'with-video' : ''}`}
        data-sensor={p.sensorKey}
        data-popup-kind={p.kind ?? (p.fromInbox ? 'inbox' : 'sensor')}
        onMouseEnter={() => ((hover.current = true), touch())}
        onMouseLeave={() => ((hover.current = false), touch())}
        onKeyDown={touch}
      >
        <div className="toolbar">
          {twoLine ? (
            <span className="sensor-popup-title sp-two-line" title={title}>
              <strong>
                {icon} {kindLine}
              </strong>
              <span className="sp-cam-name" data-testid="popup-cam-name">
                {p.name}
              </span>
              {!inScope && (
                <span className="sp-scope-path muted" data-testid="popup-scope-path">
                  {t('sensor.out_of_scope_path', { path: homePath })}
                </span>
              )}
            </span>
          ) : (
            <strong className="sensor-popup-title">
              {icon} {title}
            </strong>
          )}
          <span className="muted small">{timeStamp(p.at)}</span>
          <span className="spacer" />
          <button className="icon-btn" aria-label={t('sensor.collapse')} title={t('sensor.collapse')} onClick={() => setCollapsed(true)} data-testid="popup-collapse">
            ▾
          </button>
          <button className="icon-btn" aria-label={t('common.close')} onClick={close}>
            ✕
          </button>
        </div>
        {cam &&
          (watching === cam.key ? (
            <LiveTile cam={cam} label={p.camNames[0]} />
          ) : (
            <div className="sensor-popup-snap" data-testid="popup-snapshot">
              <EventThumb api={cam.apiEndpoint} homeId={cam.homeId} device={cam.aid} atMs={p.at} alt={p.camNames[0]} className="sensor-popup-thumb" verify />
              <span className="muted small sensor-popup-nosnap">{t('sensor.no_snapshot')}</span>
            </div>
          ))}
        {p.cameras.length > 0 && (
          <div className="toolbar wrap">
            {cam && watching !== cam.key && (
              <button className="btn btn-sm btn-primary" onClick={() => (setWatching(cam.key), touch())} data-testid="popup-live">
                ▶ {t('sensor.watch_live')}
              </button>
            )}
            {p.cameras.map((c, i) => (
              <button key={c.key} className="btn btn-sm" onClick={() => (nav(playbackHref(c.key, p.at / 1000)), close())}>
                ⏪ {t('sensor.playback', { cam: p.camNames[i] ?? c.name })}
              </button>
            ))}
          </div>
        )}
        {!p.cameras.length && !p.fromInbox && <p className="muted small">{t('sensor.no_link')}</p>}
        {p.fromInbox && !p.cameras.length && (
          <div className="toolbar wrap">
            <button
              className="btn btn-sm"
              onClick={() => (nav(p.id.startsWith('inbox:') ? `/devices/${encodeURIComponent(p.homeId)}/${encodeURIComponent(p.sensorKey.slice(p.homeId.length + 1))}` : `/events/${encodeURIComponent(p.homeId)}/${encodeURIComponent(p.id)}`), close())}
              data-testid="popup-view"
            >
              {t('inbox.view')}
            </button>
          </div>
        )}
        {p.level === 'alarm' && !p.id.startsWith('inbox:') && (
          <div className="toolbar wrap">
            <AlarmActions popup homeId={p.homeId} eventId={p.id} />
          </div>
        )}
        {(!inScope || more > 0) && (
          <div className="toolbar small">
            {!inScope && (
              <button className="btn btn-sm" onClick={() => setScope('home:' + p.homeId, '', { announce: t('scope.switched_to', { name: homePath }) })} data-testid="popup-switch-scope">
                {t('sensor.switch_scope_to', { path: homePath })}
              </button>
            )}
            <span className="spacer" />
            {moreBtn(false)}
          </div>
        )}
      </div>
    </div>
  );
}
